import type { SafetyAlertDispatchLogItem } from '../services/safetyAlertDispatchService';

export type SafetyAlertChannelKey = 'sms' | 'kakao' | 'wearable';

export type PoolSafetyAlertChannelsResponse = {
  sms: boolean;
  kakao: boolean;
  wearable: boolean;
  system_defaults: Record<SafetyAlertChannelKey, boolean>;
  overrides: Partial<Record<SafetyAlertChannelKey, boolean>> | null;
};

function parseDetailJson(raw?: string | null): Record<string, unknown> | null {
  if (!raw || !String(raw).trim()) return null;
  try {
    const v = JSON.parse(raw) as unknown;
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function wearableStatusLabel(status: unknown, connected?: unknown): string {
  const s = String(status || '').toLowerCase();
  if (s === 'offline' || connected === false) return '오프라인';
  if (s === 'online' || connected === true) return '전달됨';
  if (s === 'logged') return '로그 기록';
  if (s) return s;
  return '알 수 없음';
}

function formatSmsKakaoLines(data: Record<string, unknown>): string[] {
  const lines: string[] = [];
  const mode = data.mode;
  if (typeof mode === 'string' && mode) lines.push(`전송 모드: ${mode}`);
  const sent = data.sent;
  const failed = data.failed;
  if (sent != null) {
    lines.push(`발송 성공: ${sent}건` + (failed ? `, 실패: ${failed}건` : ''));
  }
  const results = data.results;
  if (Array.isArray(results)) {
    for (const r of results) {
      if (!r || typeof r !== 'object') continue;
      const row = r as Record<string, unknown>;
      const phone = row.phone ?? row.receiver;
      const st = row.status ?? row.result;
      if (phone != null) lines.push(`· ${phone}: ${st ?? '—'}`);
    }
  }
  const detail = data.detail;
  if (typeof detail === 'string' && detail.trim()) lines.push(detail.trim());
  return lines;
}

function formatWearableLines(data: Record<string, unknown>): string[] {
  const lines: string[] = [];
  const delivered = data.delivered_wearables;
  const target = data.target_count;
  if (delivered != null) {
    lines.push(
      `웨어러블 전달: ${delivered}대` +
        (target != null ? ` / 대상 ${target}대` : '')
    );
  }
  if (data.pool_websocket === true) {
    lines.push('시설 WebSocket 알림: 전송함');
  }
  const results = data.results;
  if (Array.isArray(results) && results.length > 0) {
    for (const r of results) {
      if (!r || typeof r !== 'object') continue;
      const row = r as Record<string, unknown>;
      const id = row.device_id ?? row.wearable_id ?? '기기';
      lines.push(`· ${id}: ${wearableStatusLabel(row.status, row.connected)}`);
    }
  }
  const detail = data.detail;
  if (typeof detail === 'string' && detail.trim()) {
    // 미등록·미연결 등 실패 사유는 항상 표시
    if (!lines.includes(detail.trim())) {
      lines.push(detail.trim());
    }
  }
  return lines;
}

/** 목록에 표시할 성공 요약 (여러 줄) */
export function formatDispatchSuccessLines(item: SafetyAlertDispatchLogItem): string[] {
  const data = parseDetailJson(item.detail_json);
  if (data) {
    const ch = item.channel;
    const fromJson =
      ch === 'wearable' ? formatWearableLines(data) : formatSmsKakaoLines(data);
    if (fromJson.length > 0) return fromJson;
  }
  if (item.detail_summary?.trim()) return [item.detail_summary.trim()];
  return ['성공'];
}

/** 실패 상세 다이얼로그용 (JSON 원문 없음) */
export function formatDispatchFailureDetail(item: SafetyAlertDispatchLogItem): string[] {
  const data = parseDetailJson(item.detail_json);
  const lines: string[] = [];
  if (data) {
    const ch = item.channel;
    const body = ch === 'wearable' ? formatWearableLines(data) : formatSmsKakaoLines(data);
    lines.push(...body);
  }
  if (lines.length === 0 && item.detail_summary?.trim()) {
    lines.push(item.detail_summary.trim());
  }
  if (lines.length === 0) lines.push('전송에 실패');
  return lines;
}

/** 상세 다이얼로그용 — 성공/실패 공통 */
export function formatDispatchDetailLines(item: SafetyAlertDispatchLogItem): string[] {
  if (item.channel === 'none') {
    const data = parseDetailJson(item.detail_json);
    const detail =
      (data && typeof data.detail === 'string' && data.detail.trim()) ||
      item.detail_summary?.trim();
    return [detail || '발송 가능한 매체가 없어 외부 알림을 보내지 않음'];
  }
  return item.success ? formatDispatchSuccessLines(item) : formatDispatchFailureDetail(item);
}

export function dispatchResultCellHint(item: SafetyAlertDispatchLogItem): string | null {
  if (item.success) return null;
  return '클릭하여 실패 상세 보기';
}
